import { useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Typography } from "@repo/design-system";

// ── Constants ─────────────────────────────────────────────────────────────────

const INTEGER_LIKE_COLS = new Set([
  "Calculated HBD",
  "Calculated HBA",
  "Calculated RotB",
]);

const BAR_COLOR = "#0096bc";
const KDE_COLOR = "#004d74";
const GRID_COLOR = "#e2e8f0";
const AXIS_COLOR = "#64748b";
const NUM_BINS = 150;
const INV_SQRT_2PI = 1 / Math.sqrt(2 * Math.PI);
export const OUTLIER_MIN_COUNT = 10;

// ── Binning ───────────────────────────────────────────────────────────────────

interface IBin {
  label: string;
  count: number;
  kde?: number;
}

function safeMin(arr: number[]): number {
  return arr.reduce((m, v) => (v < m ? v : m), arr[0] ?? 0);
}
function safeMax(arr: number[]): number {
  return arr.reduce((m, v) => (v > m ? v : m), arr[0] ?? 0);
}

function trimEdges(bins: IBin[]): IBin[] {
  let lo = 0;
  let hi = bins.length - 1;
  while (lo <= hi && bins[lo].count < OUTLIER_MIN_COUNT) lo++;
  while (hi >= lo && bins[hi].count < OUTLIER_MIN_COUNT) hi--;
  return bins.slice(lo, hi + 1);
}

function buildBins(values: number[], isInteger: boolean): IBin[] {
  if (values.length === 0) return [];

  const min = safeMin(values);
  const max = safeMax(values);

  if (isInteger) {
    const iMin = Math.floor(min);
    const iMax = Math.ceil(max);
    const bins = Array.from({ length: iMax - iMin + 1 }, (_, i) => ({
      label: String(iMin + i),
      count: values.filter((v) => Math.round(v) === iMin + i).length,
    }));
    return trimEdges(bins);
  }

  const range = max - min || 1;
  const binWidth = range / NUM_BINS;

  const bins: IBin[] = Array.from({ length: NUM_BINS }, (_, i) => {
    const lo = min + i * binWidth;
    const hi = lo + binWidth;
    const mid = (lo + hi) / 2;
    const count = values.filter((v) =>
      i === NUM_BINS - 1 ? v >= lo && v <= hi : v >= lo && v < hi,
    ).length;
    return { label: formatX(mid), count, kde: 0 };
  });

  // Scott's rule Gaussian KDE, scaled to count space
  const n = values.length;
  const mean = values.reduce((s, v) => s + v, 0) / n;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance) || 1;
  const bw = 1.06 * std * Math.pow(n, -0.2);

  bins.forEach((bin, i) => {
    const mid = min + (i + 0.5) * binWidth;
    const density =
      values.reduce((sum, xi) => {
        const z = (mid - xi) / bw;
        return sum + INV_SQRT_2PI * Math.exp(-0.5 * z * z);
      }, 0) /
      (n * bw);
    bin.kde = density * n * binWidth;
  });

  return trimEdges(bins);
}

function formatX(val: number): string {
  if (Math.abs(val) >= 100) return val.toFixed(0);
  if (Math.abs(val) >= 10) return val.toFixed(1);
  return val.toFixed(2);
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function HistogramTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const countEntry = payload.find((p) => p.dataKey === "count");
  return (
    <div className="rounded-lg border border-borderPrimary bg-backgroundPrimary px-3 py-2 shadow-medium">
      <Typography
        variant="paragraph-m-semibold"
        className="text-contentSecondary">
        {label}
      </Typography>
      <Typography variant="paragraph-m-regular" className="text-contentPrimary">
        Count: {countEntry?.value ?? 0}
      </Typography>
    </div>
  );
}

// ── Chart ─────────────────────────────────────────────────────────────────────

interface IHistogramChartProps {
  colName: string;
  values: number[];
}

export function HistogramChart({ colName, values }: IHistogramChartProps) {
  const isInteger = INTEGER_LIKE_COLS.has(colName);
  const bins = useMemo(() => buildBins(values, isInteger), [values, isInteger]);

  const tickInterval = isInteger
    ? 0
    : Math.max(1, Math.floor(NUM_BINS / 6) - 1);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-borderPrimary bg-backgroundPrimary p-4 shadow-light">
      <Typography
        variant="paragraph-m-semibold"
        className="text-contentPrimary">
        Distribution of {colName}
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart
          data={bins}
          barCategoryGap={isInteger ? "20%" : 0}
          margin={{ top: 4, right: 8, left: 0, bottom: 36 }}>
          <CartesianGrid
            stroke={GRID_COLOR}
            strokeDasharray=""
            vertical={false}
          />
          <XAxis
            dataKey="label"
            interval={tickInterval}
            tick={{ fontSize: 10, fill: AXIS_COLOR }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
            label={{
              value: colName,
              position: "insideBottom",
              offset: -24,
              fontSize: 11,
              fill: AXIS_COLOR,
            }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: AXIS_COLOR }}
            tickLine={false}
            axisLine={false}
            width={56}
            label={{
              value: "Count of Molecules",
              angle: -90,
              position: "insideLeft",
              offset: 14,
              fontSize: 11,
              fill: AXIS_COLOR,
              style: { textAnchor: "middle" },
            }}
          />
          <Tooltip
            content={<HistogramTooltip />}
            cursor={{ fill: "#e2e8f020" }}
          />
          <Bar
            dataKey="count"
            fill={BAR_COLOR}
            fillOpacity={0.75}
            isAnimationActive={false}
          />
          {!isInteger && (
            <Line
              dataKey="kde"
              type="monotone"
              stroke={KDE_COLOR}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
